// Copyright (c) Docugami, Inc. All rights reserved.

import { ITagText } from "hooks/TagText";
import { useChunkSelection } from "hooks/chunkSelection";
import { useSlotForChunk } from "hooks/chunkSlots";
import { ChunkInstance } from "networking/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import styled from "styled-components";
import { DragWrapper } from "./CardStyles";
import { ChunkTagInputField } from "./ChunkTagInputField";
import { DndChunk, DndType } from "./DndTypes";
import { IInputField } from "./HintInputField";
import { ItemCardlet } from "./ItemCardlet";
import { ISlotItem, isChunkSlot } from "./ItemSlot";
import { ItemSlots } from "./ItemSlots";
import { SlidingPanel } from "./SlidingPanel";
import { TagsViewer } from "./TagsViewer";
import { useExampleChunksForLabel } from "hooks/exampleChunksForLabel";
import { ExampleChunksModal } from "components/common/ExampleChunksModal";
import { dgIconNames } from "hooks/dgIcons";
import { themeBlue } from "components/shell/presentation/AppStyles";
import { DgIconButton } from "components/shell/utils/DgIconButton";
import { TooltipHost } from "@fluentui/react";

const Body = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
`

const StyledItemSlots = styled(ItemSlots)`
    margin-top: 5px;
`

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
`

const LabelContainer = styled.div`
    flex: 1;
`

export interface SelectedItemCardletProps {
    clusterId: string;
    documentId: string;
    instance?: ChunkInstance;
    footer?: JSX.Element;
    dndDisabled?: boolean;
    tagText: ITagText;
    editDisabled?: boolean;
}

export const SelectedItemCardlet = (props: SelectedItemCardletProps): JSX.Element => {
    const [exampleModalOpen, setExampleModalOpen] = useState(false);

    const { selectChunk } = useChunkSelection();
    const instance = props.instance;

    const tagText = props.tagText;
    const tagInputRef = useRef<IInputField>();

    const userLabel = useMemo(() => {
        const label = instance?.chunk?.labels[0];
        if (label?.toolId === 'cp') {
            return label.value;
        }

        return '';
    }, [instance?.chunk?.labels])

    const { exampleChunks } = useExampleChunksForLabel(userLabel, false, !!userLabel);

    function onClick(item: ISlotItem) {
        if (isChunkSlot(item)) {
            selectChunk(item.id);
        }
    }

    const slotItem = useSlotForChunk(instance?.spanId);
    const slots = useMemo(() => !!slotItem ? [slotItem] : [], [slotItem]);

    const [{ isDragging }, dragRef, dragVisualRef] = useDrag<DndChunk, any, { isDragging: boolean }>({
        type: DndType.Chunk,
        item: {
            type: DndType.Chunk,
            instance: instance!,
            label: tagText.tagText
        },
        canDrag: () => !props.dndDisabled && tagText.isValid,
        collect: m => ({
            isDragging: m.isDragging()
        })
    }, [tagText.isEditing, tagText.tagText, tagText.isValid, instance, props.dndDisabled]);

    useEffect(() => {
        dragVisualRef(getEmptyImage());
    }, [dragVisualRef, tagText.isEditing, tagText.tagText, tagText.isValid, instance])

    function getBody(): JSX.Element {
        return (
            <Body>
                <SlidingPanel durationS={.2} scrollable maxHeight={50} isOpen={tagText.isEditing}>
                    <TagsViewer tags={[
                        {
                            label: "ALTERNATE TAGS:",
                            tags: tagText.targetTags,
                            onSelected: tag => {
                                tagText.selectTag(tag, "Suggested");
                                tagInputRef.current?.focus();
                            }
                        },
                        {
                            label: "RECENT TAGS:",
                            tags: tagText.recentTags,
                            onSelected: tag => {
                                tagText.selectTag(tag, "Recent");
                                tagInputRef.current?.focus();
                            }
                        }
                    ]} />
                </SlidingPanel>
                <StyledItemSlots
                    slots={slots}
                    onClick={onClick}
                    hideManagementCommands={true} />
            </Body>
        )
    }

    const header = (
        <HeaderContainer>
            <LabelContainer>
                <ChunkTagInputField disabled={props.editDisabled} inputRef={tagInputRef} tagText={tagText} />
            </LabelContainer>
            {
                !!exampleChunks?.length &&
                <TooltipHost content="View examples">
                    <DgIconButton
                        iconName={dgIconNames.DgExampleChunks}
                        height={13}
                        width={18}
                        color={themeBlue}
                        onClick={() => setExampleModalOpen(true)}
                        label='' />
                </TooltipHost>
            }
        </HeaderContainer>
    )

    return (
        <DragWrapper isDragging={isDragging}>
            <ItemCardlet
                dragHandleRef={!!tagText.tagText ? dragRef : undefined}
                header={header}
                footer={props.footer}>
                {getBody()}
            </ItemCardlet>
            <ExampleChunksModal isOpen={exampleModalOpen} onDismiss={() => setExampleModalOpen(false)} label={userLabel} autoDismissWhenEmpty={true} />
        </DragWrapper>
    );
}
